import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Info, Plus, Settings2, Trash2, Warehouse } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTenant } from "./AppShell";
import { Badge, Button, Card, cx, Input, Skeleton } from "./ui";
import { api, ApiError, canManage, type OdooDescribe, type OdooFieldMap , type OdooProductFieldMap } from "@/lib/api";

import { t, msg } from "@/lib/i18n";
const errMsg = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Fehler") : "Fehler");

const MODELS: { key: OdooFieldMap["model"]; label: string }[] = [
  { key: "sale.order", label: msg("Verkaufsauftrag") },
  { key: "stock.picking", label: msg("Lieferschein") },
  { key: "res.partner", label: msg("Kontakt (Lieferadresse)") },
];
const TARGETS: { key: OdooFieldMap["target"]; label: string; hint: string }[] = [
  { key: "note", label: msg("Hinweis ans Lager"), hint: msg("erscheint beim Auftrag und geht als Hinweis an Logiful") },
  { key: "tag", label: msg("Tag für Regeln"), hint: msg("z. B. „Geschenk“ → Auftragsregel legt Grußkarte bei") },
  { key: "info", label: msg("Zusatzinfo am Auftrag"), hint: msg("nur zur Anzeige im Portal, KI-Assistent und Schnittstelle") },
  { key: "shipping_method", label: msg("Versandart"), hint: msg("Wert wird als Versandart des Shops genutzt (Versandregeln)") },
];
const STATES: { key: "assigned" | "confirmed" | "waiting"; label: string; hint: string }[] = [
  { key: "assigned", label: msg("Bereit"), hint: msg("Ware in Odoo reserviert – Standard") },
  { key: "confirmed", label: msg("Wartet auf Verfügbarkeit"), hint: msg("wenn Odoo den Logiful-Bestand nicht führt") },
  { key: "waiting", label: msg("Wartet auf anderen Vorgang"), hint: msg("z. B. mehrstufige Lieferung") },
];

/** Odoo: Auslöser, Logiful-Lager, Lieferschein-Zustände und Zuordnung eigener Felder */
export function OdooSetupCard({ channelId, config }: { channelId: string; config: Record<string, unknown> }) {
  const { tenantId, role } = useTenant();
  const qc = useQueryClient();
  const editable = canManage(role);
  const d = useQuery({ queryKey: ["odoo-describe", tenantId, channelId], queryFn: () => api.channels.odooDescribe(tenantId, { config, channelId }), staleTime: 5 * 60_000, retry: false });
  const [trigger, setTrigger] = useState<"picking" | "sale_order">((config.trigger as "picking" | "sale_order") ?? "picking");
  const [warehouseId, setWarehouseId] = useState<number | null>((config.warehouseId as number | null) ?? null);
  const [states, setStates] = useState<string[]>((config.pickingStates as string[]) ?? ["assigned"]);
  const [map, setMap] = useState<OdooFieldMap[]>((config.fieldMap as OdooFieldMap[]) ?? []);
  const [importProducts, setImportProducts] = useState(config.importProducts === true);
  const [companyIds, setCompanyIds] = useState<number[]>((config.companyIds as number[]) ?? []);
  const [takeLots, setTakeLots] = useState(config.takeLots !== false);
  const [productMap, setProductMap] = useState<OdooProductFieldMap[]>((config.productFieldMap as OdooProductFieldMap[]) ?? []);
  const [useReserved, setUseReserved] = useState(config.useReservedLotsOnShip === true);
  const [productName, setProductName] = useState<"name" | "variant" | "full">((config.productName as "name" | "variant" | "full") ?? "variant");
  const [orderNumber, setOrderNumber] = useState<"picking" | "sale">((config.orderNumber as "picking" | "sale") ?? "picking");
  useEffect(() => {
    setOrderNumber((config.orderNumber as "picking" | "sale") ?? "picking");
    setTakeLots(config.takeLots !== false);
    setProductMap((config.productFieldMap as OdooProductFieldMap[]) ?? []);
    setUseReserved(config.useReservedLotsOnShip === true);
    setProductName((config.productName as "name" | "variant" | "full") ?? "variant");
    setCompanyIds((config.companyIds as number[]) ?? []);
    setTrigger((config.trigger as "picking" | "sale_order") ?? "picking");
    setWarehouseId((config.warehouseId as number | null) ?? null);
    setStates((config.pickingStates as string[]) ?? ["assigned"]);
    setMap((config.fieldMap as OdooFieldMap[]) ?? []);
    setImportProducts(config.importProducts === true);
  }, [config]);
  const save = useMutation({
    mutationFn: () => api.channels.updateMarketplace(tenantId, channelId, { config: { ...config, productFieldMap: productMap, companyIds, orderNumber, takeLots, useReservedLotsOnShip: useReserved, productName, trigger, warehouseId, pickingStates: states.length ? states : ["assigned"], fieldMap: map, importProducts } }),
    onSuccess: () => { toast.success("Odoo-Einstellungen gespeichert"); void qc.invalidateQueries({ queryKey: ["marketplace", tenantId, channelId] }); void qc.invalidateQueries({ queryKey: ["odoo-describe", tenantId, channelId] }); },
    onError: (e) => toast.error("Nicht gespeichert", { description: errMsg(e) }),
  });
  const dirty = trigger !== (config.trigger ?? "picking") || warehouseId !== (config.warehouseId ?? null) || JSON.stringify(states) !== JSON.stringify(config.pickingStates ?? ["assigned"]) || JSON.stringify(map) !== JSON.stringify(config.fieldMap ?? []) || importProducts !== (config.importProducts === true) || orderNumber !== ((config.orderNumber as string) ?? "picking") || takeLots !== (config.takeLots !== false) || JSON.stringify(productMap) !== JSON.stringify(config.productFieldMap ?? []) || useReserved !== (config.useReservedLotsOnShip === true) || productName !== ((config.productName as string) ?? "variant") || JSON.stringify([...companyIds].sort()) !== JSON.stringify([...((config.companyIds as number[]) ?? [])].sort());
  const companies = d.data?.companies ?? [];
  // Lager nur der gewählten Unternehmen (ohne Auswahl: alle)
  const shownWarehouses = (d.data?.warehouses ?? []).filter((w) => !companyIds.length || w.companyId == null || companyIds.includes(w.companyId));

  return (
    <Card className="p-5 space-y-5">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold text-[14px] flex items-center gap-2"><Settings2 className="size-4 text-fg-muted" /> {t("Odoo einrichten")}</h2>
          <p className="text-[12.5px] text-fg-muted">{t("Was an Logiful geht und welche eigenen Felder mitkommen.")}{d.data?.version ? t(" Verbunden mit Odoo {version}.", { version: d.data.version }) : ""}</p>
        </div>
      </div>

      <section>
        <p className="text-[12.5px] font-medium">{t("Was geht an Logiful?")}</p>
        <div className="mt-1.5 grid sm:grid-cols-2 gap-2">
          {([
            { key: "picking", label: msg("Lieferschein (empfohlen)"), hint: msg("jeder bereite Ausgang des Logiful-Lagers – Teillieferungen und Rückstände wie in Odoo") },
            { key: "sale_order", label: msg("Bestätigter Verkaufsauftrag"), hint: msg("jede bestätigte Bestellung; der Lieferschein wird beim Versand erledigt") },
          ] as const).map((o) => (
            <label key={o.key} className={cx("flex gap-2.5 rounded-xl border p-3 cursor-pointer", trigger === o.key ? "border-ink bg-surface-2/60" : "border-border hover:bg-surface-2/40")}>
              <input type="radio" className="mt-1 accent-ink" disabled={!editable} checked={trigger === o.key} onChange={() => setTrigger(o.key)} />
              <span><span className="block text-[13px] font-medium">{t(o.label)}</span><span className="block text-[12px] text-fg-muted">{t(o.hint)}</span></span>
            </label>
          ))}
        </div>
      </section>

      {companies.length > 1 && (
        <section>
          <p className="text-[12.5px] font-medium">{t("Unternehmen in Odoo")}</p>
          <p className="text-[11.5px] text-fg-subtle">{t("Nur Lieferscheine, Lager, Artikel und Bestände der gewählten Unternehmen. Ohne Auswahl: alle, auf die der Benutzer Zugriff hat.")}</p>
          <div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-1">
            {companies.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-[13px]">
                <input type="checkbox" className="accent-ink" disabled={!editable} checked={companyIds.includes(c.id)} onChange={() => setCompanyIds((x) => (x.includes(c.id) ? x.filter((y) => y !== c.id) : [...x, c.id]))} />
                {c.name}
              </label>
            ))}
          </div>
          {companyIds.length > 0 && warehouseId && !shownWarehouses.some((w) => w.id === warehouseId) && <p className="mt-1 text-[12px] text-warning">{t("Das gewählte Logiful-Lager gehört zu keinem der gewählten Unternehmen – bitte Lager neu wählen.")}</p>}
        </section>
      )}

      <section className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-[12.5px] font-medium flex items-center gap-1.5"><Warehouse className="size-3.5 text-fg-subtle" /> {t("Logiful-Lager in Odoo")}</span>
          {d.isLoading ? <Skeleton className="mt-1 h-9" /> : (
            <select disabled={!editable} value={warehouseId ?? ""} onChange={(e) => setWarehouseId(e.target.value ? Number(e.target.value) : null)} className="mt-1 h-9 w-full rounded-lg border border-border bg-surface px-2 text-[13px]">
              <option value="">{t("Alle Lager (nicht empfohlen)")}</option>
              {shownWarehouses.map((w) => <option key={w.id} value={w.id}>{w.name}{w.code ? ` (${w.code})` : ""}{companies.length > 1 && w.companyId ? ` · ${companies.find((c) => c.id === w.companyId)?.name ?? ""}` : ""}</option>)}
            </select>
          )}
          <span className="block mt-1 text-[11.5px] text-fg-subtle">{t("Nur Ausgänge dieses Lagers gehen an Logiful – eigene Lager bleiben unberührt.")}</span>
        </label>
        {trigger === "picking" && (
          <label className="block">
            <span className="text-[12.5px] font-medium">{t("Auftragsnummer in Connect")}</span>
            <select disabled={!editable} value={orderNumber} onChange={(e) => setOrderNumber(e.target.value as "picking" | "sale")} className="mt-1 h-9 w-full rounded-lg border border-border bg-surface px-2 text-[13px]">
              <option value="picking">{t("Lieferscheinnummer (z. B. WH/OUT/00012)")}</option>
              <option value="sale">{t("Verkaufsauftrag (z. B. S00042)")}</option>
            </select>
            <span className="block mt-1 text-[11.5px] text-fg-subtle">{orderNumber === "picking" ? t("Eindeutig je Lieferung – auch bei Teillieferungen und Rückständen.") : t("Wie der Endkunde sie kennt – Rückstände bekommen „-2“, „-3“.")} {t("Die andere Nummer steht immer am Auftrag und ist suchbar. Gilt für neue Aufträge.")}</span>
          </label>
        )}
        {trigger === "picking" && (
          <fieldset>
            <legend className="text-[12.5px] font-medium">{t("Lieferscheine im Zustand")}</legend>
            <div className="mt-1 space-y-1">
              {STATES.map((s) => (
                <label key={s.key} className="flex items-start gap-2 text-[13px]">
                  <input type="checkbox" className="mt-1 accent-ink" disabled={!editable} checked={states.includes(s.key)} onChange={() => setStates((x) => (x.includes(s.key) ? x.filter((y) => y !== s.key) : [...x, s.key]))} />
                  <span>{t(s.label)} <span className="text-[12px] text-fg-subtle">– {t(s.hint)}</span></span>
                </label>
              ))}
            </div>
          </fieldset>
        )}
      </section>

      {trigger === "picking" && (
        <section>
          <label className="flex items-start gap-2.5 text-[13px]">
            <input type="checkbox" className="mt-1 accent-ink" disabled={!editable} checked={takeLots} onChange={(e) => setTakeLots(e.target.checked)} />
            <span>
              <span className="font-medium">{t("Chargen aus dem Lieferschein vorgeben")}</span>
              <span className="block text-[12px] text-fg-muted">{t("Im Lieferschein reservierte oder gewählte Chargen gehen mit MHD als Vorgabe an die Lager-Position. Liegt die Charge nicht (ausreichend) im Lager, wartet der Auftrag mit klarem Grund.")}</span>
            </span>
          </label>
          <label className="mt-3 flex items-start gap-2.5 text-[13px]">
            <input type="checkbox" className="mt-1 accent-ink" disabled={!editable} checked={useReserved} onChange={(e) => setUseReserved(e.target.checked)} />
            <span>
              <span className="font-medium">{t("Ohne Charge vom Lager: reservierte Charge verwenden")}</span>
              <span className="block text-[12px] text-fg-muted">{t("Meldet das Lager bei einem chargenpflichtigen Artikel keine Charge, erledigt Connect den Lieferschein mit der in Odoo reservierten Charge (Vermerk im Odoo-Verlauf).")} <b className="text-warning">{t("Vorsicht:")}</b> {t("Die reservierte muss nicht die tatsächlich versendete Charge sein – wichtig für Rückrufe. Ohne Häkchen lehnt Connect die Meldung ab und zeigt den Grund am Auftrag.")}</span>
            </span>
          </label>
        </section>
      )}

      <section>
        <label className="block max-w-md">
          <span className="text-[12.5px] font-medium">{t("Artikelname")}</span>
          <select disabled={!editable} value={productName} onChange={(e) => setProductName(e.target.value as "name" | "variant" | "full")} className="mt-1 h-9 w-full rounded-lg border border-border bg-surface px-2 text-[13px]">
            <option value="variant">{t("mit Variante – „Coldbrew Cacao (0.25L)“")}</option>
            <option value="full">{t("wie in Odoo – „[A13011] Coldbrew Cacao (0.25L)“")}</option>
            <option value="name">{t("nur Produktname – „Coldbrew Cacao“")}</option>
          </select>
          <span className="block mt-1 text-[11.5px] text-fg-subtle">{t("Für Auftragspositionen und übernommene Artikel. Im Portal gepflegte Namen bleiben unverändert.")}</span>
        </label>
      </section>

      <section>
        <label className="flex items-start gap-2.5 text-[13px]">
          <input type="checkbox" className="mt-1 accent-ink" disabled={!editable} checked={importProducts} onChange={(e) => setImportProducts(e.target.checked)} />
          <span>
            <span className="font-medium">{t("Artikel aus Odoo übernehmen")}</span>
            <span className="block text-[12px] text-fg-muted">{t("Lagerartikel mit EAN, Gewicht, Zoll, Ursprung und Preis – inkl. Chargen-, MHD- und Seriennummernpflicht aus Odoo („Nachverfolgung“, „Ablaufdaten“). Im Portal gepflegte Felder werden nicht überschrieben. Bestand geht je Charge mit MHD an den Logiful-Lagerort zurück.")}</span>
          </span>
        </label>
      </section>

      <ProductFieldMapEditor map={productMap} setMap={setProductMap} fields={d.data?.productFields ?? []} loading={d.isLoading} editable={editable} />

      <FieldMapEditor map={map} setMap={setMap} describe={d.data} loading={d.isLoading} error={d.error ? errMsg(d.error) : null} editable={editable} />

      {editable && (
        <div className="flex items-center justify-end gap-2 pt-1 border-t border-border">
          {dirty && <span className="text-[12px] text-fg-muted mr-auto">{t("Nicht gespeicherte Änderungen")}</span>}
          <Button variant="primary" size="sm" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>{t("Speichern")}</Button>
        </div>
      )}
    </Card>
  );
}

const PRODUCT_TARGETS: { key: OdooProductFieldMap["target"]; label: string; unit?: "weight" | "size"; standard?: string }[] = [
  { key: "grossWeightKg", label: msg("Gewicht (brutto)"), unit: "weight", standard: "weight" },
  { key: "netWeightKg", label: msg("Gewicht, falls kein Brutto gepflegt"), unit: "weight" },
  { key: "lengthCm", label: msg("Länge"), unit: "size" },
  { key: "widthCm", label: msg("Breite"), unit: "size" },
  { key: "heightCm", label: msg("Höhe"), unit: "size" },
  { key: "gtin", label: msg("EAN / Barcode"), standard: "barcode" },
  { key: "hsCode", label: msg("Zolltarifnummer"), standard: "hs_code" },
  { key: "countryOfOrigin", label: msg("Ursprungsland"), standard: "country_of_origin" },
  { key: "manufacturer", label: msg("Hersteller") },
  { key: "mpn", label: msg("Hersteller-Art.-Nr.") },
  { key: "customsDescription", label: msg("Zollbeschreibung") },
];

/**
 * Artikel-Felder: welches Odoo-Feld (auch Studio „x_…“) welches Stammdatum füllt – mit Einheit bei Gewicht und Maßen.
 * Ohne Zuordnung gilt der Odoo-Standard (z. B. „weight“ → Bruttogewicht).
 */
function ProductFieldMapEditor({ map, setMap, fields, loading, editable }: { map: OdooProductFieldMap[]; setMap: (m: OdooProductFieldMap[]) => void; fields: { name: string; label: string; type: string; custom: boolean }[]; loading: boolean; editable: boolean }) {
  const numeric = fields.filter((f) => ["float", "integer", "monetary"].includes(f.type));
  const of = (tv: OdooProductFieldMap["target"]) => map.find((m) => m.target === tv);
  const set = (tv: OdooProductFieldMap["target"], patch: Partial<OdooProductFieldMap> | null) => {
    const rest = map.filter((m) => m.target !== tv);
    if (!patch) return setMap(rest);
    const cur = of(tv);
    const next = { ...(cur ?? { target: tv, field: "" }), ...patch } as OdooProductFieldMap;
    setMap(next.field ? [...rest, next] : rest);
  };
  return (
    <section>
      <p className="text-[12.5px] font-medium">{t("Artikel-Felder")}</p>
      <p className="text-[11.5px] text-fg-subtle">{t("Welches Odoo-Feld welches Stammdatum füllt – auch eigene Studio-Felder. Gilt für übernommene Artikel und unbekannte Artikel aus Aufträgen. Nach dem Speichern liest der nächste Abgleich die Artikel neu ein; im Portal gepflegte Felder bleiben unverändert.")}</p>
      {loading ? <Skeleton className="mt-2 h-24" /> : (
        <div className="mt-2 rounded-xl border border-border divide-y divide-border">
          {PRODUCT_TARGETS.map((tv) => {
            const m = of(tv.key);
            const options = tv.unit ? numeric : fields;
            return (
              <div key={tv.key} className="flex flex-wrap items-center gap-2 px-3 py-2 text-[13px]">
                <span className="w-40 shrink-0 font-medium">{t(tv.label)}</span>
                <select disabled={!editable} value={m?.field ?? ""} onChange={(e) => set(tv.key, e.target.value ? { field: e.target.value, ...(tv.unit && !m?.unit ? { unit: tv.unit === "weight" ? "kg" : "cm" } : {}) } : null)} className="h-8 min-w-[220px] flex-1 rounded-lg border border-border bg-surface px-2 text-[13px]" aria-label={t("Odoo-Feld für {label}", { label: tv.label })}>
                  <option value="">{tv.standard ? t("Standard ({standard})", { standard: tv.standard }) : t("nicht übernehmen")}</option>
                  {options.map((f) => <option key={f.name} value={f.name}>{t(f.label)} ({f.name})</option>)}
                </select>
                {tv.unit && m && (
                  <select disabled={!editable} value={m.unit ?? (tv.unit === "weight" ? "kg" : "cm")} onChange={(e) => set(tv.key, { unit: e.target.value as OdooProductFieldMap["unit"] })} className="h-8 rounded-lg border border-border bg-surface px-2 text-[13px]" aria-label={t("Einheit")}>
                    {(tv.unit === "weight" ? ["kg", "g"] : ["cm", "mm", "m"]).map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function FieldMapEditor({ map, setMap, describe, loading, error, editable }: { map: OdooFieldMap[]; setMap: (m: OdooFieldMap[]) => void; describe: OdooDescribe | undefined; loading: boolean; error: string | null; editable: boolean }) {
  const [model, setModel] = useState<OdooFieldMap["model"]>("sale.order");
  const [field, setField] = useState("");
  const [onlyCustom, setOnlyCustom] = useState(true);
  const fields = useMemo(() => (describe?.fields[model] ?? []).filter((f) => !onlyCustom || f.custom), [describe, model, onlyCustom]);
  const fieldLabel = (m: OdooFieldMap) => describe?.fields[m.model]?.find((f) => f.name === m.field)?.label;
  const add = () => {
    const f = describe?.fields[model]?.find((x) => x.name === field);
    if (!f || map.some((m) => m.model === model && m.field === field)) return;
    setMap([...map, { model, field, label: f.label.slice(0, 60), target: "info" }]);
    setField("");
  };
  return (
    <section>
      <p className="text-[12.5px] font-medium">{t("Eigene Felder übernehmen")}</p>
      <p className="text-[12px] text-fg-muted">{t("Felder aus Odoo (auch Studio-Felder „x_…“) und wofür Connect sie nutzt. Gilt für neue Aufträge.")}</p>
      {error && <p className="mt-2 text-[12.5px] text-danger">{t("Felder konnten nicht geladen werden:")} {error}</p>}
      {map.length > 0 && (
        <ul className="mt-2 rounded-xl border border-border divide-y divide-border">
          {map.map((m, i) => (
            <li key={`${m.model}.${m.field}`} className="flex flex-wrap items-center gap-2 px-3 py-2 text-[13px]">
              <span className="min-w-0 flex-1">
                <Input value={m.label} disabled={!editable} onChange={(e) => setMap(map.map((x, k) => (k === i ? { ...x, label: e.target.value.slice(0, 60) } : x)))} className="h-8 max-w-[220px]" aria-label={t("Bezeichnung")} />
                <span className="block mt-0.5 text-[11.5px] text-fg-subtle font-mono">{t(MODELS.find((x) => x.key === m.model)?.label)} · {m.field}{fieldLabel(m) && fieldLabel(m) !== m.label ? ` („${fieldLabel(m)}“)` : ""}</span>
              </span>
              <ArrowRight className="size-3.5 text-fg-subtle" />
              <select disabled={!editable} value={m.target} onChange={(e) => setMap(map.map((x, k) => (k === i ? { ...x, target: e.target.value as OdooFieldMap["target"] } : x)))} className="h-8 rounded-lg border border-border bg-surface px-2 text-[13px]" title={t(TARGETS.find((tv) => tv.key === m.target)?.hint)}>
                {TARGETS.map((tv) => <option key={tv.key} value={tv.key}>{t(tv.label)}</option>)}
              </select>
              {editable && <Button size="sm" variant="ghost" icon={<Trash2 className="size-3.5" />} onClick={() => setMap(map.filter((_, k) => k !== i))} aria-label={t("Zuordnung entfernen")} />}
            </li>
          ))}
        </ul>
      )}
      {editable && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <label className="block">
            <span className="text-[11.5px] text-fg-muted">{t("Aus")}</span>
            <select value={model} onChange={(e) => { setModel(e.target.value as OdooFieldMap["model"]); setField(""); }} className="mt-0.5 block h-8 rounded-lg border border-border bg-surface px-2 text-[13px]">
              {MODELS.map((m) => <option key={m.key} value={m.key}>{t(m.label)}</option>)}
            </select>
          </label>
          <label className="block min-w-[220px] flex-1">
            <span className="text-[11.5px] text-fg-muted">{t("Feld")}</span>
            {loading ? <Skeleton className="mt-0.5 h-8" /> : (
              <select value={field} onChange={(e) => setField(e.target.value)} className="mt-0.5 block h-8 w-full rounded-lg border border-border bg-surface px-2 text-[13px]">
                <option value="">{fields.length ? t("Feld wählen …") : onlyCustom ? t("keine eigenen Felder – alle anzeigen") : t("keine Felder")}</option>
                {fields.map((f) => <option key={f.name} value={f.name}>{t(f.label)} ({f.name})</option>)}
              </select>
            )}
          </label>
          <label className="flex items-center gap-1.5 text-[12px] text-fg-muted h-8"><input type="checkbox" className="accent-ink" checked={onlyCustom} onChange={(e) => setOnlyCustom(e.target.checked)} /> {t("nur eigene Felder")}</label>
          <Button size="sm" icon={<Plus className="size-3.5" />} disabled={!field} onClick={add}>{t("Hinzufügen")}</Button>
        </div>
      )}
      <p className="mt-2 flex gap-1.5 text-[11.5px] text-fg-subtle"><Info className="size-3.5 shrink-0 mt-px" /> {t("Lieferschein-Nummer und Kundenreferenz kommen immer mit.")} <Badge className="ml-1">{t("Tag")}</Badge> {t("kann in Auftragsregeln genutzt werden (Einstellungen → Auftragsregeln).")}</p>
    </section>
  );
}
